import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseconfig';
import { USER_ROLES } from '../utils/roles';
import { useAuth } from '../contexts/AuthContext';
import './UserManagement.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    role: USER_ROLES.USER,
    password: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { currentUser, createNewUser } = useAuth();

  useEffect(() => {
    console.log("User Role Test 2: " + currentUser?.role)
    fetchUsers();
  }, [currentUser?.role]);

  const fetchUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate all required fields
    if (!newUser.email?.trim()) {
      setError('Email is required');
      return;
    }
    if (!newUser.name?.trim()) {
      setError('Name is required');
      return;
    }
    if (!newUser.password?.trim()) {
      setError('Password is required');
      return;
    }
    if (newUser.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (!newUser.role) {
      setError('Role is required');
      return;
    }

    try {
      setLoading(true);
      
      await createNewUser(
        newUser.email.trim(),
        newUser.password.trim(),
        newUser.name.trim(),
        newUser.role
      );
      
      setSuccess('User added successfully!');
      setNewUser({ 
        email: '', 
        name: '', 
        role: USER_ROLES.USER,
        password: ''
      });
      fetchUsers();
    } catch (err) {
      console.error('Error adding user:', err);
      switch (err.code) {
        case 'auth/email-already-in-use':
          setError('An account with this email already exists');
          break;
        case 'auth/invalid-email':
          setError('Invalid email address');
          break;
        case 'auth/weak-password':
          setError('Password is too weak. It must be at least 6 characters');
          break;
        default:
          setError(err.message || 'Failed to add user');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    // Prevent changing own role
    const userToUpdate = users.find(u => u.id === userId);
    if (userToUpdate?.uid === currentUser.uid) {
      setError("You cannot change your own role");
      return;
    }

    try {
      setLoading(true);
      await updateDoc(doc(db, 'users', userId), {
        role: newRole,
        updatedAt: serverTimestamp()
      });
      setSuccess('User role updated successfully!');
      fetchUsers();
    } catch (err) {
      setError('Failed to update user role');
      console.error('Error updating user role:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    // Prevent deleting own account
    const userToDelete = users.find(u => u.id === userId);
    if (userToDelete?.uid === currentUser.uid) {
      setError("You cannot delete your own account");
      return;
    }

    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      setLoading(true);
      await deleteDoc(doc(db, 'users', userId));
      setSuccess('User deleted successfully!');
      fetchUsers();
    } catch (err) {
      setError('Failed to delete user');
      console.error('Error deleting user:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-management-container">
      <div className="user-management-card">
        <h2>User Management</h2>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form onSubmit={handleAddUser} className="add-user-form">
          <h3>Add New User</h3>
          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password *</label>
            <input
              type="password"
              id="password"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              required
              minLength={6}
              placeholder="Minimum 6 characters"
            />
          </div>

          <div className="form-group">
            <label htmlFor="role">Role *</label>
            <select
              id="role"
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              required
            >
              {Object.values(USER_ROLES).map(role => (
                <option key={role} value={role}>
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? 'Adding...' : 'Add User'}
          </button>
        </form>

        <div className="users-list">
          <h3>Users</h3>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>
                    <select
                      value={user.role}
                      onChange={(e) => handleUpdateUserRole(user.id, e.target.value)}
                      disabled={currentUser.uid === user?.uid} // Disable changing own role
                    >
                      {Object.values(USER_ROLES).map(role => (
                        <option key={role} value={role}>
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="delete-button"
                      disabled={loading || user.uid === currentUser?.uid} // Disable deleting own account
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;